import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, ApiError } from "../api/client";
import { ErrorState } from "../components/AsyncState";
import { Breadcrumbs } from "../components/WorkflowUI";
import { formatBytes, outcomeLabel } from "../utils/format";
import { setOrganizationContextDirty } from "../utils/organizationContext";
import { DirectoryError, readSessionDirectory, type SessionDirectory } from "../upload/directory";
import { clearResumeState, createResumeState, loadResumeState, saveResumeState, type UploadResumeState } from "../upload/resume";
import { uploadSession, type UploadProgress } from "../upload/uploader";

export function UploadPage() {
  const [directory, setDirectory] = useState<SessionDirectory | null>(null);
  const [resume, setResume] = useState<UploadResumeState | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [projectName, setProjectName] = useState("项目");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const { organizationId = "", projectId = "" } = useParams();

  useEffect(() => {
    let cancelled = false;
    void apiClient.getProject(projectId).then((project) => {
      if (project.organization_id !== organizationId) throw new ApiError("项目不属于当前组织", 404);
      if (!cancelled) setProjectName(project.name);
    }).catch((reason: Error) => { if (!cancelled) setError(reason.message); });
    return () => { cancelled = true; };
  }, [organizationId, projectId]);
  useEffect(() => {
    setOrganizationContextDirty("upload", directory || busy ? "录制上传尚未完成" : null);
    return () => setOrganizationContextDirty("upload", null);
  }, [directory, busy]);

  async function chooseDirectory(event: ChangeEvent<HTMLInputElement>) {
    setError(""); setProgress(null);
    try {
      const selected = await readSessionDirectory(event.target.files ?? []);
      setDirectory(selected);
      setResume(loadResumeState(selected.manifest.session_id, organizationId, projectId));
    } catch (reason) {
      setDirectory(null);
      setResume(null);
      setError(reason instanceof DirectoryError ? reason.message : "目录读取失败");
    }
  }

  async function beginUpload() {
    if (!directory) return;
    setBusy(true); setError("");
    try {
      const activeState = resume ?? await prepareUpload(directory);
      setResume(activeState);
      await assertResumable(activeState);
      await uploadSession(directory, activeState, setProgress);
      await apiClient.finalize(activeState.sessionId);
      clearResumeState(activeState.sessionId, organizationId, projectId);
      navigate(`/organizations/${organizationId}/projects/${projectId}/sessions/${encodeURIComponent(activeState.sessionId)}`, { replace: true });
    } catch (reason) {
      setError(reason instanceof ApiError || reason instanceof Error ? reason.message : "上传失败，可点击重试继续");
    } finally { setBusy(false); }
  }

  async function prepareUpload(selected: SessionDirectory): Promise<UploadResumeState> {
    if (!projectId) throw new Error("请从项目页面进入上传");
    const created = await apiClient.createSession(selected.rawManifest, projectId);
    const state = createResumeState(created.session.session_id, organizationId, projectId, created.upload);
    saveResumeState(state);
    return state;
  }

  async function assertResumable(state: UploadResumeState): Promise<void> {
    const remote = await apiClient.getSession(state.sessionId);
    if (remote.status === "READY") {
      clearResumeState(state.sessionId, organizationId, projectId);
      navigate(`/organizations/${organizationId}/projects/${projectId}/sessions/${encodeURIComponent(state.sessionId)}`, { replace: true });
      throw new Error("录制数据已完成上传");
    }
    if (remote.status !== "UPLOADING") throw new Error("录制数据当前不能继续上传");
  }

  function resetSelection() {
    setDirectory(null); setResume(null); setProgress(null); setError("");
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <>
      <Breadcrumbs items={[{ label: "组织", to: "/organizations" }, { label: projectName, to: `/organizations/${organizationId}/projects/${projectId}` }, { label: "上传录制" }]} />
      <header className="page-header"><div><p className="eyebrow">数据上传</p><h1>上传录制</h1><p>选择 deepvision_ros2 生成的完整录制目录。</p></div></header>
      <section className="upload-layout">
        <div className="card upload-card">
          <div className="step-label"><span>1</span> 选择目录</div>
          <input ref={(node) => { inputRef.current = node; node?.setAttribute("webkitdirectory", ""); node?.setAttribute("directory", ""); }} className="directory-input" data-testid="directory-input" type="file" multiple onChange={chooseDirectory} />
          <button className="drop-zone" type="button" onClick={() => inputRef.current?.click()} disabled={busy}>
            <span className="upload-symbol" aria-hidden="true">⇧</span><strong>选择本地录制目录</strong><small>必须包含 manifest.json、_SUCCESS 和 manifest.files 声明的全部文件</small>
          </button>
          {error && <ErrorState message={error} retry={directory && !busy ? () => void beginUpload() : undefined} />}
        </div>
        <aside className="card contract-card"><p className="eyebrow">原始会话 v1 / v2</p><h2>上传前检查</h2><ul><li>目录带有完成标记 <code>_SUCCESS</code></li><li>v2 自动校验并导入统一标签</li><li>所有文件大小匹配 manifest</li><li>云端再次执行 SHA256 校验</li></ul></aside>
      </section>
      {directory && <section className="card selection-card">
        <div className="selection-head"><div><p className="eyebrow">已选择</p><h2>{directory.manifest.name}</h2><code>{directory.manifest.session_id}</code></div>{!busy && <button className="button ghost" type="button" onClick={resetSelection}>重新选择</button>}</div>
        <dl className="metrics"><div><dt>文件</dt><dd>{directory.files.size}</dd></div><div><dt>总大小</dt><dd>{formatBytes(directory.totalBytes)}</dd></div><div><dt>时长</dt><dd>{directory.manifest.duration_s.toFixed(1)} 秒</dd></div><div><dt>结果</dt><dd>{outcomeLabel(directory.manifest.outcome)}</dd></div></dl>
        {resume && <div className="resume-note">检测到未完成上传，将从已完成的文件或 OSS 分片继续。</div>}
        {progress && <div className="progress-block"><div><span>{progress.filePath}</span><strong>{progress.percent.toFixed(1)}%</strong></div><progress max="100" value={progress.percent}>{progress.percent}%</progress><small>{formatBytes(progress.uploadedBytes)} / {formatBytes(progress.totalBytes)}</small></div>}
        <div className="actions"><button className="button primary" type="button" disabled={busy} onClick={() => void beginUpload()}>{busy ? "正在上传并校验…" : resume ? "继续上传" : "开始上传"}</button></div>
      </section>}
    </>
  );
}
